import { Injectable } from '@angular/core';
import { buildCounterDates, buildDates, formatDate } from '@app/shared/helpers/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import moment from 'moment';
import { from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { DashboardActions } from '../actions';
import { TranslateService } from '@ngx-translate/core';


@Injectable()
export class DashboardEffects {

  constructor(
    private actions$: Actions,
    private translateService: TranslateService,
  ) { }

  loadTransferCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTransferCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'transfers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
      
            const transferCounter = statistic.data.pop();
            return DashboardActions.loadTransferCounterSuccess({ transferCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadTransferCounterFailure({ error: error.message }))
          ));
      })
    )
  );

  loadSizeCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadSizeCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'bytesUploaded';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const sizeCounter = statistic.data.pop();
            return DashboardActions.loadSizeCounterSuccess({ sizeCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadSizeCounterFailure({ error: error.message }))
          ));
      })
    )
  );

  loadFileCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadFileCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'files';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const fileCounter = statistic.data.pop();
            return DashboardActions.loadFileCounterSuccess({ fileCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadFileCounterFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTotalActiveUsersCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTotalActiveUsersCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'totalActiveUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const totalActiveUsersCounter = statistic.data.pop();
            return DashboardActions.loadTotalActiveUsersCounterSuccess({ totalActiveUsersCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadTotalActiveUsersCounterFailure({ error: error.message }))
          ));
      })
    )
  );
  // ----
  loadActiveUsersCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadActiveUsersCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'activeUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeUsersCounter = statistic.data.pop();
            return DashboardActions.loadActiveUsersCounterSuccess({ activeUsersCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadActiveUsersCounterFailure({ error: error.message }))
          ));
      })
    )
  );

  loadActiveGuestsCounter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadActiveGuestsCounter),
      concatMap(({ team, unit, period }) => {
        const { startDate, endDate } = buildCounterDates(period);
        const metricName = 'activeGuests';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        let req = team === 'ALL' ? from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })) : from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit }));
        return req.pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeGuestsCounter = statistic.data.pop();
            return DashboardActions.loadActiveGuestsCounterSuccess({ activeGuestsCounter });
          }),
          catchError(error =>
            of(DashboardActions.loadActiveGuestsCounterFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountTransferCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountTransferCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'transfers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const transferCountSeries = statistic.data;
            return DashboardActions.loadAccountTransferCountSuccess({ transferCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountTransferCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountSizeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountSizeCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'bytesUploaded';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const sizeCountSeries = statistic.data;
            return DashboardActions.loadAccountSizeCountSuccess({ sizeCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountSizeCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountFileCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountFileCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'files';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const fileCountSeries = statistic.data;
            return DashboardActions.loadAccountFileCountSuccess({ fileCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountFileCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountActiveUsersCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountActiveUsersCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'activeUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeUsersCountSeries = statistic.data;
            return DashboardActions.loadAccountActiveUsersCountSuccess({ activeUsersCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountActiveUsersCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountActiveGuestsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountActiveGuestsCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'activeGuests';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeGuestsCountSeries = statistic.data;
            return DashboardActions.loadAccountActiveGuestsCountSuccess({ activeGuestsCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountActiveGuestsCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadAccountTotalActiveUsersCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadAccountTotalActiveUsersCount),
      concatMap((action) => {
        const { period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'totalActiveUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getMetricStatistic({ metricName, statisticName, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const totalActiveUsersCountSeries = statistic.data;
            return DashboardActions.loadAccountTotalActiveUsersCountSuccess({ totalActiveUsersCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadAccountTotalActiveUsersCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTeamTransferCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamTransferCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'transfers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const transferCountSeries = statistic.data;
            return DashboardActions.loadTeamTransferCountSuccess({ transferCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamTransferCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTeamSizeCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamSizeCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'bytesUploaded';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const sizeCountSeries = statistic.data;
            return DashboardActions.loadTeamSizeCountSuccess({ sizeCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamSizeCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTeamFileCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamFileCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'files';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const fileCountSeries = statistic.data;
            return DashboardActions.loadTeamFileCountSuccess({ fileCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamFileCountFailure({ error: error.message }))
          ));
      })
    )
  );


  loadTeamActiveUsersCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamActiveUsersCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'activeUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeUsersCountSeries = statistic.data;
            return DashboardActions.loadTeamActiveUsersCountSuccess({ activeUsersCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamActiveUsersCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTeamActiveGuestsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamActiveGuestsCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'activeGuests';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const activeGuestsCountSeries = statistic.data;
            return DashboardActions.loadTeamActiveGuestsCountSuccess({ activeGuestsCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamActiveGuestsCountFailure({ error: error.message }))
          ));
      })
    )
  );

  loadTeamTotalActiveUsersCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardActions.loadTeamTotalActiveUsersCount),
      concatMap((action) => {
        const { team, period, unit } = action;
        const { startDate, endDate } = buildDates(period);
        const metricName = 'totalActiveUsers';
        const statisticName = 'sum';
        const transferSdk = new Transfer();
        return from(transferSdk.getTeamMetricStatistic({ metricName, statisticName, teamId: team, startDate, endDate, unit })).pipe(
          map(({ statistic }) => {
            const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
            statistic.data = statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
            const totalActiveUsersCountSeries = statistic.data;
            return DashboardActions.loadTeamTotalActiveUsersCountSuccess({ totalActiveUsersCountSeries });
          }),
          catchError(error =>
            of(DashboardActions.loadTeamTotalActiveUsersCountFailure({ error: error.message }))
          ));
      })
    )
  );


  buildDates(period) {
    let startDate;
    let endDate;
    switch (period) {
      case '7_DAYS':
        startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
      case '4_WEEKS':
        startDate = moment().startOf('isoWeek').subtract(3, 'weeks').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
      case '3_MONTHS':
        startDate = moment().startOf('month').subtract(2, 'months').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
      case '12_MONTHS':
        startDate = moment().startOf('month').subtract(11, 'months').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
      default:
        startDate = moment().subtract(7, 'd').format('YYYY-MM-DD');
        endDate = moment().format('YYYY-MM-DD');
        return { startDate, endDate };
    }
  }
}
